import { Deck } from "@deck.gl/core";
import { MapboxLayer } from "@deck.gl/mapbox";
import "./Pointclouds.css";
import { CesiumIonLoader, Tiles3DLoader } from "@loaders.gl/3d-tiles";
import { Tile3DLayer } from "@deck.gl/geo-layers";
import React from "react";
import { useRef, useState, useEffect } from "react";
import { Button } from "@mui/material";
import { toUnitless } from "@mui/material/styles/cssUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import Tooltip from "@mui/material/Tooltip";
const host = process.env.REACT_APP_HOST;

const token =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiJjNzZiNTA1NS01YTQ0LTQ3NDUtYmIwZS1jNmFiNDg1MDA4MGEiLCJpZCI6MTY5MDcyLCJpYXQiOjE2OTU4ODk5NTV9.IoQFbCf6Xuo0t3lQSG7lPSeLjyzGS42b4QG0LXypl20";

// --- 1. COORDINATE DICTIONARY ---
const siteViewports = {
  Smuszewo: { center: [17.404294, 52.898355], zoom: 16, pitch: 60 },
  Kalapodi: { center: [22.9617, 38.6233], zoom: 17, pitch: 60 },
  Ventotene: { center: [13.4258, 40.7935], zoom: 16, pitch: 60 },
  Aegina: { center: [23.5400, 37.7500], zoom: 16, pitch: 60 },
  Choirokitia: { center: [33.3300, 34.7900], zoom: 17, pitch: 60 },
  Epidaurus: { center: [23.0700, 37.5900], zoom: 16, pitch: 60 },
  Roseninsel: { center: [11.3000, 47.9300], zoom: 16, pitch: 60 },
  Argillies: { center: [24.0000, 35.0000], zoom: 16, pitch: 60 },
};

// --- 2. RESET VIEW ---
const INITIAL_VIEW = {
  center: [22.2379578747328, 42.534599159899095],
  zoom: 4,
  pitch: 0,
  bearing: 0
};

const Pointclouds = ({ pointClouds }) => {
  const [tileName, setTileName] = useState(null);
  const [layerStates, setLayerStates] = useState({
    Smuszewo: false,
    Kalapodi: false,
    Ventotene: false,
    Aegina: false,
    Choirokitia: false,
    Epidaurus: false,
    Roseninsel: false,
    Argillies: false,
  });

  let isRotating = false; 

  // --- 3. HELPER FUNCTIONS ---

  const removePointcloud = (name) => {
    if (window.map && window.map.getLayer(name)) {
      window.map.removeLayer(name);
    }
  };

  function rotateCamera() {
    if (isRotating) {
      window.map.rotateTo((Date.now() / 100) % 360, { duration: 0 });
      requestAnimationFrame(rotateCamera);
    }
    window.map.once("click", () => {
      isRotating = false;
    });
  }

  const handlechange = (name) => {
    // Current active logic
    const isCurrentlyActive = tileName === name;

    if (isCurrentlyActive) {
      // 1. User clicked the SAME active tile -> Turn it OFF
      setLayerStates((prev) => ({ ...prev, [name]: false }));
      removePointcloud(name);
      setTileName(null);
      // Optional: Fly back to home when closing manually
      window.map.flyTo(INITIAL_VIEW);
    } else {
      // 2. User clicked a DIFFERENT tile -> Switch to it
      // Remove old tile if exists
      if (tileName) {
         removePointcloud(tileName);
         setLayerStates((prev) => ({ ...prev, [tileName]: false }));
      }
      
      // Set new state
      setTileName(name);
      setLayerStates((prev) => ({ ...prev, [name]: true }));
    }
  };

  // --- 4. EFFECTS ---

  useEffect(() => {
    if (!tileName) return;

    const layerId = tileName;
    
    const layerConfig = {
      id: layerId,
      type: Tile3DLayer, 
      data: `${host}/get3dtiles/${tileName}/tileset.json`,
      loader: Tiles3DLoader,
      loadOptions: { "3d-tiles": { isTileset: true } },
      pointSize: 1,
      sizeUnits: "meters",
      onTileLoad: () => {
         window.map.triggerRepaint();
      }
    };

    if (!window.map.getLayer(layerId)) {
       window.map.addLayer(new MapboxLayer(layerConfig));
    }
    
    window.map.setLayoutProperty(layerId, "visibility", "visible");

    window.map.once("moveend", () => {
      isRotating = true;
      rotateCamera();
    });

    const targetView = siteViewports[tileName] || siteViewports["Smuszewo"]; 

    window.map.flyTo({
      center: targetView.center,
      essential: true,
      zoom: targetView.zoom,
      pitch: targetView.pitch,
    });

  }, [tileName]);


  useEffect(() => {
    // External Reset Logic
    if (!pointClouds) {
      if (tileName) {
        isRotating = false;
        removePointcloud(tileName);
        setTileName(null);
        setLayerStates((prev) => ({ ...prev, [tileName]: false }));
        window.map.flyTo(INITIAL_VIEW);
      }
      return;
    }

    if (pointClouds && pointClouds !== tileName) {
      if (tileName) {
        isRotating = false;
        removePointcloud(tileName);
        setLayerStates((prev) => ({ ...prev, [tileName]: false }));
      }
      setTileName(pointClouds);
      setLayerStates((prev) => ({ ...prev, [pointClouds]: true }));
    }
  }, [pointClouds]);

  // --- 5. CAROUSEL LOGIC ---
  useEffect(() => {
    const carousel = document.querySelector(".pointclouds-inside"),
      arrowIcons = document.querySelectorAll(".angle");

    let firstImg = carousel.querySelectorAll("img")[0];
    let firstImgWidth = firstImg ? firstImg.clientWidth + 3 : 0;

    const updateFirstImgWidth = () => {
      firstImg = carousel.querySelector("img");
      if (firstImg) {
        firstImgWidth = firstImg.clientWidth + 3;
      }
    };
    updateFirstImgWidth();

    arrowIcons.forEach((icon) => {
      icon.addEventListener("click", () => {
        updateFirstImgWidth();
        carousel.scrollLeft +=
          icon.id === "left" ? -firstImgWidth : firstImgWidth;
      });
    });
  }, []);

  // --- RENDER ---
  // Helper to keep tooltip styling consistent
  const tooltipProps = {
    tooltip: {
      sx: {
        bgcolor: "rgba( 83, 139, 34, 0.9)",
        fontFamily: "Maven pro",
        padding: "5px 10px",
        "& .MuiTooltip-arrow": {
          color: "rgba( 83, 139, 34, 0.9)",
        },
      },
    },
  };

  return (
    <div className="pointclouds-container">
      <div className="angle-container-left" id="left chevron">
        <FontAwesomeIcon icon={faAngleLeft} className="angle" id="left" />
      </div>
      <div className="pointclouds-inside">
        
        {/* Kalapodi */}
        <Tooltip
          title={<span style={{ fontSize: "15px" }}><b>Kalapodi</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Kalapodi.png")}
            alt="Kalapodi"
            className={tileName === "Kalapodi" ? "active" : ""}
            onClick={() => handlechange("Kalapodi")} 
          />
        </Tooltip>

        {/* Ventotene */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Ventotene</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Ventotene.png")}
            alt="Ventotene"
            className={tileName === "Ventotene" ? "active" : ""}
            onClick={() => handlechange("Ventotene")} 
          />
        </Tooltip>

        {/* Aegina */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Aegina</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Aegina.png")}
            alt="Aegina"
            className={tileName === "Aegina" ? "active" : ""}
            onClick={() => handlechange("Aegina")} 
          />
        </Tooltip>

        {/* Choirokitia */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Choirokitia</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Choirokitia.png")}
            alt="Choirokitia"
            className={tileName === "Choirokitia" ? "active" : ""}
            onClick={() => handlechange("Choirokitia")} 
          />
        </Tooltip>

        {/* Epidaurus */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Epidaurus</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Epidaurus.png")}
            alt="Epidaurus"
            className={tileName === "Epidaurus" ? "active" : ""}
            onClick={() => handlechange("Epidaurus")} 
          />
        </Tooltip>

        {/* Roseninsel */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Roseninsel</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Roseninsel.png")}
            alt="Roseninsel"
            className={tileName === "Roseninsel" ? "active" : ""}
            onClick={() => handlechange("Roseninsel")} 
          />
        </Tooltip>

        {/* Argillies */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Argillies</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Argilliez.png")}
            alt="Argillies"
            className={tileName === "Argillies" ? "active" : ""}
            onClick={() => handlechange("Argillies")} 
          />
        </Tooltip>

        {/* Smuszewo */}
        <Tooltip
          title={<span style={{ fontSize: "14px" }}><b>Smuszewo</b></span>}
          placement="top"
          arrow
          componentsProps={tooltipProps}
        >
          <img
            src={require("../PilotSites/Smuszewo.png")}
            alt="smuszewo"
            className={tileName === "Smuszewo" ? "active" : ""}
            onClick={() => handlechange("Smuszewo")}
          />
        </Tooltip>

      </div>
      <div className="angle-container-right" id="right chevron">
        <FontAwesomeIcon icon={faAngleRight} className="angle" id="right" />
      </div>
    </div>
  );
};

export default Pointclouds;